import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import TurnstileWidget from "@/components/common/TurnstileWidget";
import { shouldEnableTurnstile } from "@/lib/turnstile";

export default function RegisterPage() {
  const navigate = useNavigate();
  const { register, requestRegisterCode, startParasutAuth } = useAuth();
  const registrationEnabled = true;
  const [loading, setLoading] = useState(false);
  const [parasutLoading, setParasutLoading] = useState(false);
  const [step, setStep] = useState("form");
  const [otpCode, setOtpCode] = useState("");
  const [expiresIn, setExpiresIn] = useState(0);
  const [formData, setFormData] = useState({
    full_name: "",
    company_name: "",
    email: "",
    password: "",
  });
  const [turnstileToken, setTurnstileToken] = useState("");
  const turnstileSiteKey =
    process.env.REACT_APP_TURNSTILE_SITE_KEY || "0x4AAAAAACgE8YFxQViaDsnD";
  const turnstileEnabled = shouldEnableTurnstile();

  const canResend = step === "otp" && expiresIn <= 0 && !loading;

  const maskedEmail = (() => {
    const email = String(formData.email || "").trim();
    const [name, domain] = email.split("@");
    if (!name || !domain) return email;
    if (name.length <= 2) return `${name[0] || ""}***@${domain}`;
    return `${name.slice(0, 2)}***${name.slice(-1)}@${domain}`;
  })();

  useEffect(() => {
    if (step !== "otp" || expiresIn <= 0) return;
    const timer = window.setInterval(() => {
      setExpiresIn((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => window.clearInterval(timer);
  }, [step, expiresIn]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    if (step === "form") {
      if (turnstileEnabled && !turnstileToken) {
        toast.error("Lütfen güvenlik doğrulamasını tamamlayın.");
        setLoading(false);
        return;
      }
      const result = await requestRegisterCode(formData.email, turnstileToken);
      if (result.success) {
        setStep("otp");
        setOtpCode("");
        setExpiresIn(result.expiresIn || 600);
        setTurnstileToken("");
      }
      setLoading(false);
      return;
    }

    const result = await register({ ...formData, code: otpCode });
    if (result.success) {
      navigate(result.redirectPath || "/panel");
    }

    setLoading(false);
  };

  const handleResendCode = async () => {
    if (!canResend) return;
    setLoading(true);
    const result = await requestRegisterCode(formData.email);
    if (result.success) {
      setOtpCode("");
      setExpiresIn(result.expiresIn || 600);
    }
    setLoading(false);
  };

  const handleParasutRegister = async () => {
    if (!registrationEnabled) {
      toast.error("Kayıt olma geçici olarak kapatıldı.");
      return;
    }
    try {
      setParasutLoading(true);
      const authUrl = await startParasutAuth("register");
      window.location.href = authUrl;
    } catch (e) {
      toast.error(e?.message || "Paraşüt girişi başlatılamadı");
      setParasutLoading(false);
    }
  };

  if (!registrationEnabled) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-6" data-testid="register-page">
        <div className="w-full max-w-md rounded-xl border border-border bg-card p-6 shadow-sm">
          <h1 className="text-xl font-semibold text-foreground">Kayıt Geçici Olarak Kapalı</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Yeni kullanıcı kaydı kısa süreliğine devre dışı bırakıldı.
          </p>
          <Link to="/giris" className="mt-4 inline-block text-sm font-medium text-[#1487a3] hover:underline">
            Giriş sayfasına dön
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex" data-testid="register-page">
      {/* Left panel - branding */}
      <div className="hidden lg:flex lg:w-[45%] bg-[#0f172a] relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#1487a3]/20 via-transparent to-[#10b981]/10" />
        <div className="relative z-10 flex flex-col justify-between p-12 w-full">
          <div>
            <img src="/obihub-logo.png" alt="ObiHub" className="h-10" />
          </div>
          <div>
            <h2 className="text-3xl font-bold text-white leading-tight mb-4">
              {"\u0130\u015fletmenizi"}<br />{"B\u00fcy\u00fctmeye Ba\u015flay\u0131n"}
            </h2>
            <p className="text-muted-foreground text-sm leading-relaxed max-w-md">
              {"Hesab\u0131n\u0131z\u0131 olu\u015fturun ve dakikalar i\u00e7inde t\u00fcm sat\u0131\u015f kanallar\u0131n\u0131z\u0131 tek bir yerden y\u00f6netmeye ba\u015flay\u0131n."}
            </p>
          </div>
          <div className="flex items-center gap-6">
            <div className="text-center">
              <div className="text-2xl font-bold text-white">7+</div>
              <div className="text-[11px] text-muted-foreground">Entegrasyon</div>
            </div>
            <div className="w-px h-8 bg-white/10" />
            <div className="text-center">
              <div className="text-2xl font-bold text-white">%99.9</div>
              <div className="text-[11px] text-muted-foreground">Uptime</div>
            </div>
            <div className="w-px h-8 bg-white/10" />
            <div className="text-center">
              <div className="text-2xl font-bold text-white">500+</div>
              <div className="text-[11px] text-muted-foreground">Magaza</div>
            </div>
          </div>
        </div>
      </div>

      {/* Right panel - form */}
      <div className="flex-1 flex items-center justify-center p-6 bg-background">
        <div className="w-full max-w-sm">
          {/* Mobile logo */}
          <div className="flex items-center justify-center mb-8 lg:hidden">
            <img src="/obihub-logo.png" alt="ObiHub" className="h-10" />
          </div>

          <div className="mb-8">
            <h1 className="text-2xl font-bold text-foreground">{"Kay\u0131t Ol"}</h1>
            <p className="text-sm text-muted-foreground mt-1">{"Yeni hesap olu\u015fturun"}</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {step === "form" ? (
              <>
                <div className="space-y-2">
                  <Label htmlFor="full_name" className="text-xs font-medium">Ad Soyad</Label>
                  <Input
                    id="full_name"
                    type="text"
                    placeholder="Ahmet Yılmaz"
                    value={formData.full_name}
                    onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                    required
                    className="h-10"
                    data-testid="register-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="company_name" className="text-xs font-medium">{"\u015eirket Ad\u0131"}</Label>
                  <Input
                    id="company_name"
                    type="text"
                    placeholder={"ABC Ticaret Ltd. \u015eti."}
                    value={formData.company_name}
                    onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                    required
                    className="h-10"
                    data-testid="register-company-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-xs font-medium">E-posta</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="ornek@sirket.com"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    className="h-10"
                    data-testid="register-email-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password" className="text-xs font-medium">{"\u015eifre"}</Label>
                  <Input
                    id="password"
                    type="password"
                    placeholder="En az 6 karakter"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    required
                    minLength={6}
                    className="h-10"
                    data-testid="register-password-input"
                  />
                </div>
                {turnstileEnabled ? (
                  <TurnstileWidget
                    siteKey={turnstileSiteKey}
                    onToken={setTurnstileToken}
                    onExpire={() => setTurnstileToken("")}
                  />
                ) : null}
              </>
            ) : (
              <div className="space-y-3">
                <p className="text-sm text-muted-foreground">
                  {maskedEmail || "e-posta adresinize"} gönderilen 6 haneli kodu girin.
                </p>
                <div className="flex justify-center">
                  <InputOTP
                    maxLength={6}
                    value={otpCode}
                    onChange={(value) => setOtpCode((value || "").replace(/\D/g, "").slice(0, 6))}
                    autoComplete="one-time-code"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    containerClassName="justify-center gap-2"
                    data-testid="register-otp-input"
                  >
                    <InputOTPGroup className="gap-2">
                      <InputOTPSlot index={0} className="h-11 w-10 rounded-lg border text-xl font-semibold border-border bg-card" />
                      <InputOTPSlot index={1} className="h-11 w-10 rounded-lg border text-xl font-semibold border-border bg-card" />
                      <InputOTPSlot index={2} className="h-11 w-10 rounded-lg border text-xl font-semibold border-border bg-card" />
                      <InputOTPSlot index={3} className="h-11 w-10 rounded-lg border text-xl font-semibold border-border bg-card" />
                      <InputOTPSlot index={4} className="h-11 w-10 rounded-lg border text-xl font-semibold border-border bg-card" />
                      <InputOTPSlot index={5} className="h-11 w-10 rounded-lg border text-xl font-semibold border-border bg-card" />
                    </InputOTPGroup>
                  </InputOTP>
                </div>
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <button
                    type="button"
                    className="hover:text-foreground disabled:opacity-50"
                    disabled={!canResend}
                    onClick={handleResendCode}
                  >
                    Kodu yeniden gönder
                  </button>
                  <span>{expiresIn > 0 ? `${expiresIn}s` : "Tekrar gönderebilirsiniz"}</span>
                </div>
              </div>
            )}
            <Button
              type="submit"
              className="w-full h-10 bg-[#1487a3] hover:bg-[#1e7ab3] text-white font-medium"
              disabled={loading || (step === "otp" && otpCode.length !== 6)}
              data-testid="register-submit-btn"
            >
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {step === "otp" ? "Kaydı Tamamla" : "Doğrulama Kodu Gönder"}
            </Button>
            {step === "otp" ? (
              <Button
                type="button"
                variant="ghost"
                className="w-full h-9"
                onClick={() => setStep("form")}
                disabled={loading}
              >
                Bilgilere geri dön
              </Button>
            ) : null}
            {step === "form" ? (
            <Button
              type="button"
              variant="outline"
              className="w-full h-10"
              onClick={handleParasutRegister}
              disabled={parasutLoading || loading}
              data-testid="register-parasut-btn"
            >
              <img src="/integrations/parasut.svg" alt="Paraşüt" className="mr-2 h-4 w-4" />
              {parasutLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Paraşüt ile Devam Et
            </Button>
            ) : null}
          </form>

          <div className="mt-6 text-center text-sm">
            <span className="text-muted-foreground">{"Zaten hesab\u0131n\u0131z var m\u0131? "}</span>
            <Link to="/giris" className="text-[#1487a3] hover:underline font-medium" data-testid="login-link">
              {"Giri\u015f Yap"}
            </Link>
          </div>

          <p className="mt-8 text-center text-[11px] text-muted-foreground">
            &copy; {new Date().getFullYear()} ObiHub. {"T\u00fcm haklar\u0131 sakl\u0131d\u0131r."}
          </p>
        </div>
      </div>
    </div>
  );
}
